import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { TileSkeletonLoader } from './TileSkeletonLoader';
import { cn } from '@/lib/utils';

// Slice 2 (BI): the hero tile gains an optional progress bar + a secondary "add" note line, and a
// per-metric value colour. Backward-compatible — omitting the new props renders the prior shape.
type HeroTone = 'cyan' | 'green' | 'muted';

const TONE_HEX: Record<HeroTone, string> = {
  cyan: '#4FA8E8',
  green: '#4A9E78',
  muted: '#5C7189',
};

interface ComplianceHeroTileProps {
  label: string;
  value: number | string;
  unit?: string;
  tone?: HeroTone;
  /** 0–100; renders the thin progress bar when provided */
  barPct?: number;
  subtitle?: string;
  tooltip?: string;
  /** secondary emphasis line (blue), e.g. "111 controls still need attention." */
  addNote?: string;
  drillDownPath?: string;
  drillDownLabel?: string;
  isLoading: boolean;
  className?: string;
  'data-testid'?: string;
}

export function ComplianceHeroTile({
  label,
  value,
  unit,
  tone = 'cyan',
  barPct,
  subtitle,
  tooltip,
  addNote,
  drillDownPath,
  drillDownLabel = 'View details',
  isLoading,
  className,
  'data-testid': testId,
}: ComplianceHeroTileProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className={cn('transition-shadow', className)}>
        <TileSkeletonLoader />
      </Card>
    );
  }

  const hex = TONE_HEX[tone];

  return (
    <Card
      className={cn('flex flex-col gap-[9px] p-[15px_17px] transition-shadow hover:shadow-lg', className)}
      data-testid={testId}
    >
      <p className="text-[12.5px] text-text-muted">{label}</p>
      <div className="flex flex-wrap items-baseline gap-[9px]">
        <span
          className="text-[34px] font-bold leading-none tabular-nums tracking-[-0.03em]"
          style={{ color: hex }}
          title={tooltip}
        >
          {value}
        </span>
        {unit && <span className="text-[12.5px] text-[#7A8CA3]">{unit}</span>}
      </div>
      {barPct != null && (
        <div className="flex h-[5px] overflow-hidden rounded-[3px] bg-[#0D1626]">
          <span
            className="block h-full"
            style={{ width: `${Math.max(0, Math.min(100, barPct))}%`, background: hex }}
          />
        </div>
      )}
      {subtitle && <p className="text-[11.5px] leading-[1.5] text-text-secondary">{subtitle}</p>}
      {addNote && (
        <p className="border-t border-[#1B2C43] pt-2 text-[11.5px] leading-[1.5] text-cert-info-fg">
          {addNote}
        </p>
      )}
      {drillDownPath && (
        <button
          onClick={() => navigate(drillDownPath)}
          className="mt-auto flex items-center gap-1 text-[12px] text-accent-cyan transition-colors hover:text-text-primary"
          data-testid="drilldown-link"
        >
          {drillDownLabel} <ArrowRight className="h-3 w-3" />
        </button>
      )}
    </Card>
  );
}
